import React, { Component, ReactElement } from "react";
import { StringUtil } from "@eres/core";
import Strings from "@my-dld/assets/strings";

interface FormHolder {
	setState: ( state: any, cb?: () => void ) => void;
	state: any;
}

export type InputClassProvider = ( isFocus: boolean, isRequired?: boolean, value?: string, error?: string | ReactElement ) => string;

export interface FormFieldProps<T> {
	id: string;
	label?: string;
	required?: boolean;
	onValueChange?: ( value: T, extra?: any ) => void;
	containerClassName?: string;
	extraInputClassName?: string;
	errorClassName?: string;
	error?: string | ReactElement;
	holder?: any;
	stateFormatter?: ( x: T ) => any;
	labelClassName?: string;
	noLabel?: boolean;
	disabled?: boolean;
	converter?: FormValueConverter<any>;
	valueManager?: ValueManager;
	enableEditProvider?: EnableEditProvider;
	errorProvider?: ErrorProvider;
	inputClassProvider?: InputClassProvider;
	hideRequiredStar?: boolean;
}

export interface ValueManager {
	read: ( props: FormFieldProps<any>, holder: any, key: string ) => any;
	write: ( props: FormFieldProps<any>, holder: any, key: string, value: any, callback?: () => void ) => void;
}

class DualStateValueManager implements ValueManager {
	read( props: FormFieldProps<any>, holder: any, key: string ) {
		const state = holder.state;
		if ( state.newData && state.newData.hasOwnProperty( key ) )
			return state.newData[ key ];
		if ( state.orgData && state.orgData.hasOwnProperty( key ) )
			return state.orgData[ key ];
		return undefined;
	}

	write( props: FormFieldProps<any>, holder: any, key: string, value: any, callback?: () => void ) {
		const newData = {
			...holder.state.newData,
			[ key ]: props.stateFormatter ? props.stateFormatter( value ) : value,
		};
		holder.setState( { newData: newData }, () => {
			if ( callback )
				callback();
		} );
	}

}

class DefaultStateValueManager implements ValueManager {
	read( props: FormFieldProps<any>, holder: any, key: string ) {
		return holder.state[ key ];
	}

	write( props: FormFieldProps<any>, holder: any, key: string, value: any, callback?: () => void ) {
		value = props.stateFormatter ? props.stateFormatter( value ) : value;
		holder.setState( { [ key ]: value }, callback );
	}

}

export const defaultValueManager = new DefaultStateValueManager();
export const dualValueManager = new DualStateValueManager();

export function readValue( props: FormFieldProps<any> ) {
	let { holder, id, valueManager } = props;
	if ( valueManager == null ) valueManager = defaultValueManager;
	return valueManager?.read( props, holder, id );
}

export function writeValue( props: FormFieldProps<any>, value: any, callback?: () => void ) {
	let { holder, id, valueManager } = props;
	if ( valueManager == null )
		valueManager = defaultValueManager;
	if ( holder && holder.state ) {
		const error = holder.state.error;
		if ( error && typeof error == "object" && error[ id ] ) {
			delete error[ id ];
		}
	}
	return valueManager.write( props, holder, id, value, callback );
}

function isViewError( holder: any, id: string ) {
	return holder && holder.state && holder.state.error && holder.state.error[ id ] !== undefined;
}

export interface EnableEditProvider {
	( props: FormFieldProps<any> ): boolean;
}

export interface ErrorProvider {
	( props: FormFieldProps<any> ): string | null;
}

export const ErrorContainerStateProvider: ErrorProvider = props => {
	let { holder, id } = props as any;
	if ( holder && holder.state && holder.state.errorContainer ) {
		return holder.state.errorContainer[ id ];
	}
	return null;
};
export const OrgDataEnableEditProvider = ( props: FormFieldProps<any> ) => {
	let { holder, id } = props as any;
	const m = DataEnableEditProvider( holder, id );
	return m;
};
export const DisableEditOrgDataProvider = ( props: FormFieldProps<any> ) => {
	let { holder, id } = props as any;
	const m = DisableEditOGProvider( holder, id );
	return m;
};

export const DataEnableEditProvider = ( holder: any, id: string ) => {
	if ( isViewError( holder, id ) ) return false;
	if ( holder && holder.state ) {
		const state = holder.state;
		if ( state.forceEdit && state.forceEdit.hasOwnProperty( id ) ) {
			if ( state.forceEdit[ id ] === true )
				return false;
		}
		if ( state.newData && state.newData.hasOwnProperty( id ) )
			return false;
		if ( state.orgData && state.orgData.hasOwnProperty( id ) ) {
			const val = state.orgData[ id ];
			if ( typeof ( val ) === "string" ) {
				return !StringUtil.isEmptyStringOrNull( val );
			} else if ( val != null ) {
				return true;
			}
		}
	}
	return false;
};
export const DisableEditOGProvider = ( holder: any, id: string ) => {
	if ( isViewError( holder, id ) ) return false;
	if ( holder && holder.state ) {
		const state = holder.state;
		if ( state.forceEdit && state.forceEdit.hasOwnProperty( id ) ) {
			if ( state.forceEdit[ id ] === true )
				return false;
		}
		if ( state.orgData && state.orgData.hasOwnProperty( id ) ) {
			const val = state.orgData[ id ];
			if ( typeof ( val ) === "string" ) {
				return !StringUtil.isEmptyStringOrNull( val );
			} else if ( val != null ) {
				return true;
			}
		}
	}
	return false;
};

class FormField<T> extends Component<FormFieldProps<T>> {
	static defaultProps: Partial<FormFieldProps<any>> = {
		containerClassName: "form-group mb-4",
		errorClassName: "invalid-feedback invalid-feedback-x",
		noLabel: false
	};

	render() {
		const {
			label, required, id, containerClassName, error, errorProvider,
			errorClassName, labelClassName, noLabel, hideRequiredStar
		} = this.props;
		let xError = error;
		if ( error == null && errorProvider )
			xError = errorProvider( this.props ) as string;
		return (
			<React.Fragment>
				<div className={ containerClassName }>
					{ !noLabel &&
						<label className={ labelClassName } htmlFor={ id }><span>{ label }</span>
							{ required && !hideRequiredStar && <span className="asterisk"> *</span> }
						</label>
					}
					{ this.props.children }
					{ xError && <div className={ errorClassName }>
						{ xError }
					</div> }
				</div>

			</React.Fragment>
		);
	}
}

export interface FormValueConverter<T> {
	toText: ( data: T | string ) => string | null;
	toData: ( text: string ) => T | null;
}

export function checkNonEmpty( holder: any, key: string, error: any ): boolean {
	let isNonEmpty = false;
	if ( holder[ key ] != undefined ) {
		let v = holder[ key ];
		if ( typeof ( v ) === "string" ) {
			isNonEmpty = !StringUtil.isEmptyStringOrNull( v );
		} else
			isNonEmpty = true;
	}
	if ( isNonEmpty === false ) {
		error[ key ] = Strings.required;
	}
	return isNonEmpty;
}

export default FormField;
